<template>
    <VJoyWrapper
        v-if="config"
        :data-testid="`suggestion-card-${type}`"
        align="stretch"
        class="suggestion-card"
        column-gap="3"
        no-margin
        wrap="nowrap"
    >
        <VJoyIconButton
            v-if="config.dismissable"
            :data-testid="`suggestion-card-dismiss-${type}`"
            class="suggestion-card__dismiss"
            icon="cross"
            label="Dismiss"
            size="xsmall"
            variant="ghost"
            @click.stop="handleDismiss()"
        />
        <VJoyIcon :name="config.icon" class="suggestion-card__icon" color="secondary" size="xsmall" />
        <VJoyWrapper align="flex-start" class="suggestion-card__content" direction="column" no-margin row-gap="2">
            <VJoyWrapper align="flex-start" direction="column" no-margin row-gap="1">
                <span class="suggestion-card__title">{{ config.title }}</span>
                <span class="suggestion-card__subtitle">{{ config.subtitle }}</span>
            </VJoyWrapper>
            <VJoyButton class="suggestion-card__cta" size="xsmall" variant="secondary" @click="handleClick">{{ config.cta }}</VJoyButton>
        </VJoyWrapper>
    </VJoyWrapper>
</template>

<script lang="ts" setup>
    import {computed} from 'vue';
    import {storeToRefs} from 'pinia';
    import {navigateTo, useDataTracking, useTranslation} from '#imports';
    import {VJoyButton, VJoyIcon, VJoyIconButton, VJoyWrapper} from '@maltjoy/core-vue';
    import type {JoyIconsId} from '@maltjoy/icons';
    import type {ProfileSuggestionType} from '@api';
    import {eventBus} from '~/config/events';
    import {EditionOpenedEntryPoint, RecommendationRequestStartedEntryPoint, TrackingEvents} from '~/config/TrackingEvents';
    import {useProfileStore} from '~/store/profileStore';

    const props = defineProps<{
        type: ProfileSuggestionType;
        maltProjectId?: string;
    }>();

    const emit = defineEmits<{dismiss: []}>();

    const {t} = useTranslation();
    const {track} = useDataTracking();
    const {profile} = storeToRefs(useProfileStore());

    function handleDismiss() {
        track(TrackingEvents.FREELANCER_NUDGE_DISMISSED, {
            nudge_type: props.type.toLowerCase(),
            freelancer_account_id: profile.value?.accountId,
            page: 'profile',
        });
        emit('dismiss');
    }

    const SUGGESTION_ENTRY_POINTS: Record<ProfileSuggestionType, EditionOpenedEntryPoint> = {
        ONGOING_EXPERIENCE: EditionOpenedEntryPoint.ONGOING_EXPERIENCE_NUDGE,
        RECENT_EXPERIENCE: EditionOpenedEntryPoint.RECENT_EXPERIENCE_NUDGE,
        PORTFOLIO: EditionOpenedEntryPoint.PORTFOLIO_NUDGE,
        MALT_PROJECT: EditionOpenedEntryPoint.MALT_PROJECT_NUDGE,
        RECOMMENDATION: EditionOpenedEntryPoint.RECOMMENDATION_NUDGE,
    };
    const entryPoint = computed(() => SUGGESTION_ENTRY_POINTS[props.type]);

    type SuggestionConfig = {icon: JoyIconsId; title: string; subtitle: string; cta: string; dismissable: boolean};

    const config = computed<SuggestionConfig>(() => {
        const configs: Record<ProfileSuggestionType, SuggestionConfig> = {
            MALT_PROJECT: {
                icon: 'logo-brew',
                title: t('profile.section.suggested.for.you.malt.project.title'),
                subtitle: t('profile.section.suggested.for.you.malt.project.subtitle'),
                cta: t('profile.section.suggested.for.you.malt.project.cta'),
                dismissable: false,
            },
            RECENT_EXPERIENCE: {
                icon: 'briefcase',
                title: t('profile.section.suggested.for.you.recent.experience.title'),
                subtitle: t('profile.section.suggested.for.you.recent.experience.subtitle'),
                cta: t('profile.section.suggested.for.you.recent.experience.cta'),
                dismissable: true,
            },
            ONGOING_EXPERIENCE: {
                icon: 'briefcase',
                title: t('profile.section.suggested.for.you.ongoing.experience.title'),
                subtitle: t('profile.section.suggested.for.you.ongoing.experience.subtitle'),
                cta: t('profile.section.suggested.for.you.ongoing.experience.cta'),
                dismissable: true,
            },
            PORTFOLIO: {
                icon: 'color',
                title: t('profile.section.suggested.for.you.portfolio.title'),
                subtitle: t('profile.section.suggested.for.you.portfolio.subtitle'),
                cta: t('profile.section.suggested.for.you.portfolio.cta'),
                dismissable: true,
            },
            RECOMMENDATION: {
                icon: 'thumbup',
                title: t('profile.section.suggested.for.you.recommendation.title'),
                subtitle: t('profile.section.suggested.for.you.recommendation.subtitle'),
                cta: t('profile.section.suggested.for.you.recommendation.cta'),
                dismissable: true,
            },
        };
        return configs[props.type];
    });

    function handleClick() {
        track(TrackingEvents.SUGGESTED_FOR_YOU_CARD_CTA_CLICKED, {
            card_type: props.type.toLowerCase(),
            page_type: 'profile',
        });
        navigateToCta();
    }

    function navigateToCta() {
        switch (props.type) {
            case 'MALT_PROJECT':
                return navigateTo({
                    path: `/profile/experience/from-project/${props.maltProjectId}`,
                    query: {entryPoint: entryPoint.value, redirectAfterSave: window.location.pathname},
                });
            case 'RECENT_EXPERIENCE':
                eventBus.emit('experiences:selection-mode', {entryPoint: entryPoint.value});
                break;
            case 'ONGOING_EXPERIENCE':
                return navigateTo({path: '/profile/experiences', query: {entryPoint: entryPoint.value}});
            case 'PORTFOLIO':
                return navigateTo({path: '/profile/portfolio/new/edit', query: {entryPoint: entryPoint.value}});
            case 'RECOMMENDATION':
                eventBus.emit('recommendations:edit-mode', {entryPoint: RecommendationRequestStartedEntryPoint.PROFILE_SUGGESTED_FOR_YOU_CARD});
                break;
        }
    }
</script>

<style lang="scss" scoped>
    .suggestion-card {
        position: relative;
        padding: var(--joy-core-spacing-4);
        border: 2px solid var(--joy-color-neutral-30);
        border-radius: var(--joy-core-radius-4);
        width: 390px;
        height: 132.5px;
        flex-shrink: 0;

        &__dismiss {
            position: absolute;
            top: var(--joy-core-spacing-2);
            right: var(--joy-core-spacing-2);
            -webkit-text-stroke: 0.5px currentColor;
        }

        &__icon {
            align-self: center;
        }

        &__content {
            flex: 1;
        }

        &__cta {
            margin-top: auto;
            border-width: 1px;
        }

        &:hover {
            background-color: var(--joy-color-neutral-10);
        }

        &__title {
            font-size: var(--joy-font-size-primary-300);
            font-weight: var(--joy-font-weight-bold);
            color: var(--joy-color-neutral-60);
            display: -webkit-box;
            overflow: hidden;
            -webkit-line-clamp: 1;
            -webkit-box-orient: vertical;
        }

        &__subtitle {
            font-size: var(--joy-font-size-primary-300);
            font-weight: var(--joy-font-weight-regular);
            color: var(--joy-color-neutral-60);
            display: -webkit-box;
            overflow: hidden;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
        }
    }
</style>
